import React from 'react';
import { motion } from 'framer-motion';
import Header from '../components/Header';
import Footer from '../components/Footer';
import { Shield, FileText } from 'lucide-react';

const Refunds = () => {
    const lastUpdated = 'January 15, 2025';

    return (
        <div className="min-h-screen bg-white">
            <Header />

            {/* Hero Section */}
            <section className="pt-32 pb-20 bg-gradient-to-br from-teal-50 to-green-50">
                <div className="container mx-auto px-6">
                    <motion.div
                        initial={{ opacity: 0, y: 30 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ duration: 0.6 }}
                        className="text-center max-w-3xl mx-auto"
                    >
                        <Shield className="w-16 h-16 text-teal-600 mx-auto mb-6" />
                        <h1 className="text-4xl md:text-6xl font-bold text-gray-900 mb-6">
                            Legal & Privacy
                        </h1>
                        <p className="text-xl text-gray-600 leading-relaxed">
                            Your privacy and trust are important to us
                        </p>
                    </motion.div>
                </div>
            </section>

            {/* Content Section */}
            <section className="py-20 bg-white">
                <div className="container mx-auto px-6">
                    <div className="max-w-4xl mx-auto">
                        {/* Refund Policy */}
                        <motion.div
                            initial={{ opacity: 0, y: 30 }}
                            whileInView={{ opacity: 1, y: 0 }}
                            viewport={{ once: true }}
                            transition={{ duration: 0.6 }}
                            className="mb-16"
                        >
                            <div className="flex items-center mb-6">
                                <FileText className="w-8 h-8 text-teal-600 mr-3" />
                                <h2 className="text-3xl md:text-4xl font-bold text-gray-900">
                                    Refund Policy
                                </h2>
                            </div>
                            <p className="text-sm text-gray-500 mb-8">Last updated: {lastUpdated}</p>

                            <div className="prose prose-lg max-w-none text-gray-600 space-y-6">
                                <div>
                                    <h3 className="text-2xl font-semibold text-gray-900 mb-3">Reliable uptime through our replacement guarantee.</h3>
                                    <p className="leading-relaxed mb-4">
                                        At Maidly, we are committed to delivering reliable, high-quality home services with complete transparency and trust. Customer satisfaction is at the heart of everything we do, and we strive to ensure a smooth and hassle-free experience for every booking made through our platform.
                                    </p>
                                    <p className="leading-relaxed mb-4">
                                        We understand that there may be instances where a service does not go as planned or a booking needs to be cancelled. This Refund Policy outlines the circumstances under which refunds may be requested, approved, and processed. It is designed to help our customers clearly understand their rights and our responsibilities.
                                    </p>
                                    <p className="leading-relaxed mb-4">
                                        By using Maidly and booking any service through our website or application, you agree to the terms mentioned in this policy. We encourage you to read this page carefully before placing a booking to avoid any confusion related to cancellations or refunds.
                                    </p>
                                    <p className="leading-relaxed">
                                        Our support team reviews all refund requests fairly and promptly to ensure the best possible resolution while maintaining the quality and integrity of our services.
                                    </p>
                                </div>

                                <div>
                                    <h3 className="text-2xl font-semibold text-gray-900 mb-3">Service Cancellation</h3>
                                    <p className="leading-relaxed mb-2">
                                        At Maidly, we understand that plans can change. Customers are allowed to cancel their booked services subject to the conditions mentioned below:
                                    </p>
                                    <ul className="list-disc pl-6 space-y-2 mb-4">
                                        <li>Service cancellations can be made through the Maidly mobile application, or by contacting our customer support team.</li>
                                        <li>Cancellations requested before the service has started are generally eligible for a refund.</li>
                                        <li>To ensure fair compensation for our service professionals, cancellations made within a short period before the scheduled service time may attract a cancellation charge.</li>
                                        <li>If the service professional is delayed beyond a reasonable time or does not arrive at the scheduled location, customers may cancel the service without any cancellation charges.</li>
                                        <li>Once a service has started or has been successfully completed, it will be considered delivered and cannot be cancelled.</li>
                                    </ul>
                                    <p className="leading-relaxed">
                                        Maidly reserves the right to determine cancellation eligibility and applicable charges based on the timing of the request and the status of the service booking.
                                    </p>
                                </div>

                                <div>
                                    <h3 className="text-2xl font-semibold text-gray-900 mb-3">Refund Eligibility</h3>
                                    <p className="leading-relaxed mb-2">
                                        At Maidly, refunds are provided only in genuine and valid cases where the service experience does not meet our promised standards. Refund eligibility is determined after reviewing the details of the booking and the issue reported.
                                    </p>
                                    <p className="leading-relaxed mb-2">
                                        A customer may be eligible for a refund under the following circumstances:
                                    </p>
                                    <ul className="list-disc pl-6 space-y-2 mb-4">
                                        <li>The service professional did not arrive at the scheduled date and time</li>
                                        <li>The booked service was cancelled by Maidly due to unavailability of service professionals</li>
                                        <li>Payment was successfully deducted, but the booking was not confirmed</li>
                                        <li>The service delivered was significantly different from what was booked</li>
                                        <li>The service was not completed due to issues caused by the service professional</li>
                                        <li>Serious service quality concerns, provided they are reported immediately or within a reasonable time after service completion</li>
                                    </ul>
                                    <p className="leading-relaxed">
                                        Refunds will be processed only after proper verification of the complaint, which may include booking details, service reports, or communication records. Maidly reserves the right to approve or reject refund requests based on the findings of the review process and the nature of the issue raised.
                                    </p>
                                </div>

                                <div>
                                    <h3 className="text-2xl font-semibold text-gray-900 mb-3">Non-Refundable Cases</h3>
                                    <p className="leading-relaxed mb-2">
                                        Refunds will not be provided by Maidly in the following situations:
                                    </p>
                                    <ul className="list-disc pl-6 space-y-2 mb-4">
                                        <li>The service has been successfully completed as per the booking details.</li>
                                        <li>The customer is dissatisfied due to personal preferences, expectations, or change of mind after service completion.</li>
                                        <li>Incorrect or incomplete information was provided by the customer at the time of booking.</li>
                                        <li>The customer was unavailable or denied entry to the service professional at the scheduled time.</li>
                                        <li>Delays caused due to factors beyond Maidly’s control, including traffic conditions, weather issues, or local restrictions.</li>
                                        <li>Issues reported after a significant delay from service completion, without valid justification.</li>
                                        <li>Add-on services requested during or after the service that were not part of the original booking.</li>
                                    </ul>
                                    <p className="leading-relaxed">
                                        Maidly reserves the right to determine whether a refund request falls under non-refundable circumstances after reviewing the booking and service details.
                                    </p>
                                </div>

                                <div>
                                    <h3 className="text-2xl font-semibold text-gray-900 mb-3">Partial Refunds</h3>
                                    <p className="leading-relaxed mb-2">
                                        In certain cases, Maidly may issue a partial refund when a service has been partially delivered or does not fully match the original booking.
                                    </p>
                                    <p className="leading-relaxed mb-2">
                                        Partial refunds may be applicable in situations including, but not limited to:
                                    </p>
                                    <ul className="list-disc pl-6 space-y-2 mb-4">
                                        <li>Only part of the booked service was completed.</li>
                                        <li>One or more services from a package were not delivered.</li>
                                        <li>The service was interrupted due to reasons attributable to the service professional.</li>
                                        <li>Minor service quality issues where a full refund is not applicable.</li>
                                    </ul>
                                    <p className="leading-relaxed">
                                        The amount of the partial refund will be determined based on the value of the service already delivered and the portion remaining incomplete. All partial refund requests are reviewed on a case-by-case basis. Once approved, partial refunds will be processed to the original payment method within the standard refund timeline followed by Maidly.
                                    </p>
                                </div>

                                <div>
                                    <h3 className="text-2xl font-semibold text-gray-900 mb-3">Refund Process</h3>
                                    <p className="leading-relaxed mb-2">
                                        Once a refund request is submitted, Maidly follows a standard review and approval process to ensure fairness and accuracy.
                                    </p>
                                    <ul className="list-disc pl-6 space-y-2 mb-4">
                                        <li>All refund requests are carefully reviewed based on booking details, service status, and the issue reported.</li>
                                        <li>Customers may be required to provide additional information, such as booking ID, service date, or supporting details, to help us process the request.</li>
                                        <li>After verification, eligible refunds will be approved by Maidly.</li>
                                        <li>Approved refunds will be processed within 5–7 business days.</li>
                                        <li>Refunds will be credited to the original payment method used at the time of booking.</li>
                                        <li>The time taken for the refund to reflect in the customer’s account may vary depending on the bank or payment provider.</li>
                                    </ul>
                                    <p className="leading-relaxed">
                                        Maidly reserves the right to reject refund requests that do not meet the eligibility criteria outlined in this policy.
                                    </p>
                                </div>

                                <div>
                                    <h3 className="text-2xl font-semibold text-gray-900 mb-3">How to Request a Refund</h3>
                                    <p className="leading-relaxed mb-2">
                                        Customers can request a refund by following the steps mentioned below:
                                    </p>
                                    <ul className="list-disc pl-6 space-y-2 mb-4">
                                        <li>Contact Maidly’s customer support through our website, mobile application, or official support channels.</li>
                                        <li>Share your Booking ID, date of service, and a clear reason for requesting the refund.</li>
                                        <li>If required, provide supporting information or details related to the service to help us review the request.</li>
                                        <li>Refund requests should be submitted within a reasonable time after the scheduled service or service completion.</li>
                                    </ul>
                                    <p className="leading-relaxed mb-4">
                                        Once the request is received, our support team will review the details and inform the customer about the status of the refund request.
                                    </p>
                                    <p className="leading-relaxed mb-4">
                                        Maidly aims to resolve all refund-related requests in a timely and transparent manner.
                                    </p>
                                    <ul className="list-none space-y-2">
                                        <li><strong>Email:</strong> <a href="mailto:support@redqubes.com" className="text-teal-600 hover:underline">support@redqubes.com</a></li>
                                        <li><strong>Phone:</strong> <a href="tel:+918278007777" className="text-teal-600 hover:underline">+91 8278007777</a></li>
                                    </ul>
                                </div>

                                <div>
                                    <h3 className="text-2xl font-semibold text-gray-900 mb-3">Policy Updates</h3>
                                    <p className="leading-relaxed mb-4">
                                        Maidly reserves the right to update or modify this Refund Policy at any time to ensure clarity, compliance, and alignment with our services.
                                    </p>
                                    <p className="leading-relaxed mb-4">
                                        Any changes will be effective immediately upon posting on the Maidly website or app. Customers are encouraged to review this Refund Policy periodically to stay informed about the latest terms.
                                    </p>
                                    <p className="leading-relaxed mb-4">
                                        Continued use of Maidly services after updates to the Refund Policy constitutes acceptance of the revised terms. For any questions or clarifications regarding changes in the policy, customers can contact our support team at any time.
                                    </p>
                                    <p className="leading-relaxed">
                                        At Maidly, we are committed to maintaining transparency and ensuring that all our customers have a clear understanding of their rights and responsibilities regarding refunds.
                                    </p>
                                </div>
                            </div>
                        </motion.div>

                        {/* Contact Section */}
                        <motion.div
                            initial={{ opacity: 0, y: 30 }}
                            whileInView={{ opacity: 1, y: 0 }}
                            viewport={{ once: true }}
                            transition={{ duration: 0.6 }}
                            className="mt-16 bg-gradient-to-br from-teal-50 to-green-50 rounded-3xl p-8 text-center"
                        >
                            <h3 className="text-2xl font-bold text-gray-900 mb-4">
                                Have Questions?
                            </h3>
                            <p className="text-gray-600 mb-6">
                                If you have any questions about our policies, please contact us
                            </p>
                            <div className="flex flex-col sm:flex-row gap-4 justify-center">
                                <a href="mailto:legal@maidly.in" className="text-teal-600 font-semibold hover:underline">
                                    legal@maidly.in
                                </a>
                                <span className="hidden sm:inline text-gray-400">|</span>
                                <a href="tel:18001234567" className="text-teal-600 font-semibold hover:underline">
                                    +91 8278007777
                                </a>
                            </div>
                        </motion.div>
                    </div>
                </div>
            </section>

            <Footer />
        </div>
    );
};

export default Refunds;
